import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons, Typography } from '../../../stories/atoms';
import { IconWithText } from '../../../stories/molecules';
import {
  commonTranslations,
  profitAndLossTransalations,
  transactionSearchTranslations,
} from '../../../translations';
import { StyledButton } from '../ProfitAndLoss.styles';
import { PLSortBy } from '../ProfitAndLoss';
import { ContainerV2, RowFlexContainer } from '../../../stories/atoms/containerV2/ContainerV2';

type PLFilterProps = {
  selectedMonths: string[];
  sortBy: PLSortBy | undefined;
  selectedYear?: string;
  monthMap: Record<string, string>;
  onClose: (filter: string, filterType: string) => void;
  onReset: () => void;
};

const PLFilter: FC<PLFilterProps> = ({ selectedMonths, sortBy, selectedYear, monthMap, onClose, onReset }) => {
  const { t } = useTranslation();
  const { selectedFilters: selectedFilterTranslation } = commonTranslations;
  const { clearFilter } = transactionSearchTranslations;
  const { sortBy: sortByTranslations } = profitAndLossTransalations;

  const selectedMonthNames = selectedMonths
    .map((month) => monthMap[month])
    .filter((monthName): monthName is string => Boolean(monthName));

  const handleClose = (filter: string, selectedFilterType: string) => {
    if (selectedFilterType === 'month') {
      const monthKey = Object.keys(monthMap).find((key) => monthMap[key] === filter);
      if (monthKey) onClose(monthKey, selectedFilterType);
    } else {
      onClose(filter, selectedFilterType);
    }
  };

  return (
    <ContainerV2 data-testid="profit-loss-filters-wrapper">
      <RowFlexContainer alignItems="center" justifyContent="between">
        <RowFlexContainer alignItems="center">
          <Typography margin={[1, 2, 1, 0]}>
            {t(selectedFilterTranslation.key, selectedFilterTranslation.defaultValue)}
          </Typography>

          <div>
            {selectedYear && (
              <StyledButton
                data-testid="selected-year"
                key={selectedYear}
                label={selectedYear}
                padding={[1, 3]}
                textOptions={{
                  type: 'button',
                  color: 'white',
                }}
                margin={[1, 2, 1, 0]}
              />
            )}
            {selectedMonthNames.map((month) => (
              <StyledButton
                data-testid="selected-month"
                key={month}
                label={month}
                padding={[1, 3]}
                iconOptions={{
                  icon: Icons.CLOSE,
                  size: 'small',
                  margin: [0, 2],
                }}
                textOptions={{
                  type: 'button',
                  color: 'white',
                }}
                labelPosition="left"
                margin={[1, 2, 1, 0]}
                onClick={() => handleClose(month, 'month')}
              />
            ))}
            {sortBy && (
              <StyledButton
                data-testid="sort-by"
                key={sortBy}
                label={t(sortByTranslations[sortBy].key, sortByTranslations[sortBy].defaultValue)}
                padding={[1, 3]}
                iconOptions={{
                  icon: Icons.CLOSE,
                  size: 'small',
                  margin: [0, 2],
                }}
                textOptions={{
                  type: 'button',
                  color: 'white',
                }}
                labelPosition="left"
                margin={[1, 2, 1, 0]}
                onClick={() => handleClose(sortBy, 'sort')}
              />
            )}
          </div>
        </RowFlexContainer>

        <IconWithText
          data-testid="clear-all-button"
          label={t(clearFilter.key, clearFilter.defaultValue)}
          iconOptions={{
            icon: Icons.TRASHBIN,
            size: 'small',
          }}
          textOptions={{
            type: 'button',
          }}
          labelPosition="left"
          onClick={onReset}
          padding={[0, 0, 0, 2]}
        />
      </RowFlexContainer>
    </ContainerV2>
  );
};

export default PLFilter;
